import { BaseMode, createDescriptorHeaderStrategy } from '../BaseMode.js';
import { getThinkingGroup, pickHeader } from '../../constants/thinkingGroups.js';

const MODE_LABELS = {
  checkin: 'Experiment Report',
  build: 'Build an Experiment'
};

/**
 * Treatment Mode - Practice-focused therapeutic sessions
 * All user messages go to LLM with sub-mode-specific context
 */
export class TreatmentMode extends BaseMode {
  /**
   * Synthetic first user message sent on every check-in entry
   * (ModeOrchestrator.handleNavigation). Visible live and on replay, never
   * `userAuthored`. The eval fixture
   * (backend/eval/scenarios/treatment/checkin-report-opener.yaml) quotes this
   * same literal — change both together.
   * @type {string}
   */
  static CHECKIN_OPENER = 'I want to report back on this experiment';

  /**
   * Input placeholder for the report conversation: the hint for the user's
   * actual report, which follows the opener and the assistant's greeting.
   * @type {string}
   */
  static CHECKIN_PLACEHOLDER = 'How did it go? Start with what happened...';

  constructor() {
    super({
      name: 'TREATMENT',
      contextBuilder: null
    });
    this.subMode = null;
  }

  /**
   * Cache the active sub-mode (checkin/build) so getModeLabel() can read it
   * without reaching into ModeOrchestrator internals. Mirrors
   * ModeOrchestrator._treatmentSubMode, but on the instance.
   */
  async onEnter(previousMode, transitionData = {}) {
    await super.onEnter(previousMode, transitionData);
    this.subMode = transitionData.subMode ?? null;

    // All four check-in entry points converge here, which is why the placeholder
    // is wired at this seam rather than at each navigation call site.
    // ModeOrchestrator.switchMode clears any prior custom placeholder just
    // before onEnter, so setting it here always wins.
    if (this.subMode === 'checkin') {
      window.app?.modules?.input?.setPlaceholder?.(TreatmentMode.CHECKIN_PLACEHOLDER);
    }
  }

  /**
   * Restore path (page reload / dashboard return into an existing session):
   * ModeOrchestrator.restoreMode() has no transitionData, so subMode is
   * threaded in via fetched session metadata instead.
   * @param {Object|null} sessionMeta - Session metadata, or null if unavailable
   */
  async onRestore(sessionMeta) {
    this.subMode = sessionMeta?.subMode ?? null;
  }

  getModeLabel() {
    return MODE_LABELS[this.subMode] || '';
  }

  getRoutingStrategy() {
    return 'llm';
  }

  filterHistory(allMessages) {
    return allMessages.filter(msg =>
      msg.mode !== 'DISCOVERY' || msg.role === 'user'
    );
  }

  /**
   * Header content strategy for Treatment Mode. Uses the live descriptor by
   * default and temporarily replaces it with practice-composition phases.
   */
  getHeaderContentStrategy() {
    const ELLIPSIS = '<span class="thinking-indicator__ellipsis"></span>';
    // Allow-list gate, not just labels: TREATMENT adopts ONLY the practice
    // phases. A bare registry lookup here would make it start rendering
    // `retrieving` / `composing_memo` headers too — do not widen this to
    // "any group with a header". Copy comes from the shared registry.
    const ALLOWED_PHASES = ['choosing_practice', 'designing_practice'];
    const base = createDescriptorHeaderStrategy('Discussing an experiment');
    let phase = null;

    return {
      generateContent: async (context) => {
        // Privacy outranks the practice phases — otherwise a private session
        // reads "Choosing an experiment…" mid-turn. Folded here rather than after
        // the phase branch because `base.generateContent` is what normally
        // consumes the privacy edge, and it is not reached on this path.
        if (base.syncPrivacy(context)) {
          return base.generateContent(context);
        }
        if (phase && ALLOWED_PHASES.includes(phase)) {
          return { text: `${pickHeader(getThinkingGroup(phase))}${ELLIPSIS}` };
        }
        return base.generateContent(context);
      },
      getUpdateTriggers: () => [
        'message:received',
        'session:privacyModeChanged',
        'privacy:changed',
        'state:update',
        'stream:complete',
        'stream:failed'
      ],
      shouldUpdate: (event, eventData) => {
        if (event === 'state:update') {
          const advisingPhase = eventData?.updates?.advisingPhase;
          if (advisingPhase === phase) return false;
          if (advisingPhase && ALLOWED_PHASES.includes(advisingPhase)) {
            phase = advisingPhase;
            return true;
          }
          return false;
        }

        // Terminal events clear a mid-turn practice phase, which the backend
        // never retracts — without this the header stayed on "Designing a
        // practice…" until the user's next message. Handled explicitly rather
        // than through the fall-through below because `base.shouldUpdate`
        // returns true unconditionally, which would re-fetch the descriptor on
        // every completed turn.
        if (event === 'stream:complete' || event === 'stream:failed') {
          if (!phase) return false;
          phase = null;
          return true;
        }

        phase = null;
        return base.shouldUpdate(event, eventData);
      }
    };
  }
}
